import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCardIcon, DevicePhoneMobileIcon, BanknotesIcon, ShieldCheckIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import PublicHeader from '../../components/public/PublicHeader';
import { useCurrency } from '../../context/CurrencyContext';
import API_URL from '../../config/api';

// Import card logos (used in order-summary trust badges only — no card
// data is collected on this page; B5)
import visaLogo from '../../assets/images/visa.png';
import mastercardLogo from '../../assets/images/mastercard.png';
import paypalLogo from '../../assets/images/paypal.png';
import mtnLogo from '../../assets/images/MTN.png';
import airtelLogo from '../../assets/images/AIRTEL.png';

const PaymentCheckout = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { decimalPlaces } = useCurrency();
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [processing, setProcessing] = useState(false);
    // B5: card fields (PAN/expiry/CVV) removed — they were never transmitted
    // and collecting them is a PCI/trust hazard. The payment provider's hosted
    // checkout collects card data securely. Only mobile-money/bank-transfer
    // details are kept for display purposes (also not transmitted in demo mode).
    const [formData, setFormData] = useState({
        phoneNumber: '',
        networkProvider: 'MTN'
    });

    const [copiedField, setCopiedField] = useState(null);

    const handleCopy = (text, field) => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    // Get payment details from URL params
    const amount = searchParams.get('amount') || '10';
    const currency = searchParams.get('currency') || 'USD';
    const plan = searchParams.get('plan') || 'Premium';
    const billingPeriod = searchParams.get('billing_period') || 'monthly';
    const customerName = searchParams.get('name') || '';
    const customerEmail = searchParams.get('email') || '';
    const txRef = searchParams.get('tx_ref') || '';
    const isDemo = searchParams.get('demo') === 'true';

    const normalizedDecimals = Number.isFinite(decimalPlaces) ? decimalPlaces : 2;
    const numericAmount = Number.parseFloat(amount);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    const formattedAmount = `${currency} ${safeAmount.toLocaleString(undefined, {
        minimumFractionDigits: normalizedDecimals,
        maximumFractionDigits: normalizedDecimals
    })}`;

    // Format billing period for display
    const formatBillingPeriod = (period) => {
        const periodMap = {
            'monthly': 'Monthly',
            'biannual': 'Bi-Annual (6 months)',
            'annual': 'Annual (12 months)',
            'lifetime': 'Lifetime'
        };
        return periodMap[period] || period;
    };

    const paymentMethods = [
        { id: 'card', name: 'Card Payment', icon: CreditCardIcon, description: 'Pay with Visa, Mastercard' },
        { id: 'mobile_money', name: 'Mobile Money', icon: DevicePhoneMobileIcon, description: 'MTN, Airtel' },
        { id: 'bank_transfer', name: 'Bank Transfer', icon: BanknotesIcon, description: 'Direct bank transfer' },
        { id: 'paypal', name: 'PayPal', icon: (props) => (
            <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
            </svg>
        ), description: 'Fast, safe payment' }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const [submitError, setSubmitError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setProcessing(true);
        setSubmitError(null);

        // In demo mode there is no payment provider to send card/mobile-money
        // data to. Instead, call the backend's /complete-demo endpoint which
        // provisions the tenant synchronously and returns login credentials.
        try {
            const response = await fetch(`${API_URL}/api/payments/complete-demo`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tx_ref: txRef, email: customerEmail }),
            });
            const data = await response.json();

            if (response.ok && data.success) {
                // Redirect to success page — pass data via router state
                // (memory-only) to avoid leaking personal info in URLs.
                navigate('/payment/success', {
                    state: {
                        tx_ref: txRef,
                        demo: 'true',
                        email: customerEmail,
                        login_url: data.login_url || '',
                    },
                });
            } else if (data.demo_mode === false) {
                // Real payment mode — the webhook handles provisioning.
                // Redirect to success page which will poll.
                navigate('/payment/success', {
                    state: { tx_ref: txRef, demo: 'true', email: customerEmail },
                });
            } else {
                setSubmitError(data.detail || 'Payment could not be completed. Please contact support.');
                setProcessing(false);
            }
        } catch (err) {
            setSubmitError(err.message || 'Failed to complete payment. Please try again.');
            setProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-sans selection:bg-teal-500/30 overflow-hidden">
            <PublicHeader />

            {/* Ambient Background */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%27200%27%20height=%27200%27%3E%3Cfilter%20id=%27n%27%3E%3CfeTurbulence%20type=%27fractalNoise%27%20baseFrequency=%270.9%27%20numOctaves=%273%27%20stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect%20width=%27100%25%27%20height=%27100%25%27%20filter=%27url(%23n)%27%20opacity=%270.4%27/%3E%3C/svg%3E')] opacity-20 brightness-100 contrast-150"></div>
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-gradient-to-b from-teal-50/50 via-transparent to-transparent dark:from-teal-900/20"></div>
                <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute top-40 -left-20 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
            </div>

            <div className="relative pt-32 pb-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    
                    {/* Left Column: Payment Form */}
                    <div className="lg:col-span-7">
                        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-gray-700/50 shadow-2xl shadow-teal-500/5 p-8">
                            <div className="mb-8">
                                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Complete Your Payment</h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Select your preferred payment method below.</p>
                                {isDemo && (
                                    <div className="mt-3 inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium border border-yellow-200">
                                        🧪 Demo Mode - No real charges
                                    </div>
                                )}
                            </div>

                            <div className="space-y-8">
                                {/* Payment Methods Selection */}
                                <div className="space-y-4">
                                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                        Select Payment Method
                                    </h2>
                                    
                                    <div className="grid grid-cols-1 gap-3">
                                        {paymentMethods.map((method) => {
                                            const Icon = method.icon;
                                            const isSelected = paymentMethod === method.id;
                                            return (
                                                <button
                                                    key={method.id}
                                                    onClick={() => setPaymentMethod(method.id)}
                                                    className={`w-full flex items-center p-4 rounded-xl border transition-all text-left relative overflow-hidden ${
                                                        isSelected
                                                            ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-900/10 ring-1 ring-teal-500'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                                                    }`}
                                                >
                                                    {isSelected && (
                                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-teal-500"></div>
                                                    )}
                                                    <div className={`p-2.5 rounded-lg mr-4 ${
                                                        isSelected 
                                                            ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30' 
                                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                                                    }`}>
                                                        <Icon className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <p className={`font-semibold text-sm ${
                                                            isSelected ? 'text-teal-900 dark:text-teal-100' : 'text-gray-900 dark:text-white'
                                                        }`}>
                                                            {method.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{method.description}</p>
                                                    </div>
                                                    {isSelected && (
                                                        <CheckCircleIcon className="h-5 w-5 text-teal-500 ml-auto" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="h-px bg-gray-100 dark:bg-gray-700/50"></div>

                                {/* Payment Details Form */}
                                <div className="space-y-4">
                                    <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
                                        Payment Details
                                    </h2>

                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        {paymentMethod === 'card' && (
                                            <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-5 flex items-start gap-3">
                                                <ShieldCheckIcon className="w-5 h-5 text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0" />
                                                <div>
                                                    <h3 className="text-sm font-bold text-teal-900 dark:text-teal-100 mb-1">
                                                        Secure Card Payment
                                                    </h3>
                                                    <p className="text-xs text-teal-700 dark:text-teal-300">
                                                        No card details are collected here. You will be securely
                                                        redirected to the payment provider's PCI-DSS compliant hosted checkout
                                                        to enter your card information. Ospito never sees or
                                                        stores your card number, expiry, or CVV.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {paymentMethod === 'mobile_money' && (
                                            <>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Network Provider</label>
                                                    <select
                                                        name="networkProvider"
                                                        value={formData.networkProvider}
                                                        onChange={handleInputChange}
                                                        className="w-full px-4 py-2.5 rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all text-sm"
                                                        required
                                                    >
                                                        <option value="MTN">MTN Mobile Money</option>
                                                        <option value="Airtel">Airtel Money</option>
                                                        <option value="Vodafone">Vodafone Cash</option>
                                                    </select>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Phone Number</label>
                                                    <div className="relative">
                                                        <input
                                                            type="tel"
                                                            name="phoneNumber"
                                                            value={formData.phoneNumber}
                                                            onChange={handleInputChange}
                                                            placeholder="+256 700 000 000"
                                                            className="w-full pl-11 pr-4 py-2.5 rounded-xl border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all text-sm"
                                                            required
                                                        />
                                                        <DevicePhoneMobileIcon className="absolute left-3.5 top-2.5 h-5 w-5 text-gray-400" />
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        {paymentMethod === 'bank_transfer' && (
                                            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
                                                <div className="flex items-start gap-4 mb-6">
                                                    <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                        <BanknotesIcon className="h-6 w-6 text-teal-600" />
                                                    </div>
                                                    <div>
                                                        <h3 className="text-sm font-bold text-gray-900 dark:text-white">Corporate Bank Details</h3>
                                                        <p className="text-xs text-gray-500 mt-1">Please make a transfer to the account below. Your account will be activated immediately after confirmation.</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-4">
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
                                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Bank Name</p>
                                                                <p className="font-semibold text-gray-900 dark:text-white">Standard Chartered</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-gray-500 mb-1">Account Name</p>
                                                                <p className="font-semibold text-gray-900 dark:text-white">Ospito Systems Ltd</p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-100 dark:border-gray-700 shadow-sm relative group cursor-pointer hover:border-teal-500 transition-colors"
                                                             onClick={() => handleCopy('0100123456789', 'account')}>
                                                            <p className="text-xs text-gray-500 mb-1">Account Number</p>
                                                            <div className="flex justify-between items-center">
                                                                <p className="font-mono font-bold text-lg text-teal-600 tracking-wider">0100123456789</p>
                                                                <span className={`text-[10px] px-2 py-1 rounded transition-all ${
                                                                    copiedField === 'account' 
                                                                        ? 'bg-teal-100 text-teal-700 opacity-100' 
                                                                        : 'bg-gray-100 text-gray-600 opacity-0 group-hover:opacity-100'
                                                                }`}>
                                                                    {copiedField === 'account' ? 'Copied!' : 'Copy'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="bg-yellow-50 dark:bg-yellow-900/10 rounded-lg p-4 border border-yellow-100 dark:border-yellow-900/20">
                                                        <div className="flex gap-3">
                                                            <div className="mt-0.5">
                                                                <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="text-xs font-bold text-yellow-800 dark:text-yellow-500 mb-1">Important Reference</p>
                                                                <p className="text-xs text-yellow-700 dark:text-yellow-400 mb-2">
                                                                    Please use this reference code in your transfer description:
                                                                </p>
                                                                <div className="bg-white/50 dark:bg-black/20 border border-yellow-200 dark:border-yellow-800 rounded px-3 py-2 font-mono text-sm font-bold text-yellow-900 dark:text-yellow-400 flex justify-between items-center cursor-pointer hover:bg-white/80 transition-colors"
                                                                     onClick={() => handleCopy(txRef, 'reference')}>
                                                                    {txRef || 'REF-PENDING'}
                                                                    <span className={`text-[10px] transition-all ${
                                                                        copiedField === 'reference' 
                                                                            ? 'text-teal-600 font-bold' 
                                                                            : 'opacity-50'
                                                                    }`}>
                                                                        {copiedField === 'reference' ? 'Copied!' : 'Copy'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {paymentMethod === 'paypal' && (
                                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 text-center border border-blue-200 dark:border-blue-800">
                                                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                                                    <img src={paypalLogo} alt="PayPal" className="h-6 w-auto" />
                                                </div>
                                                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">Pay with PayPal</h3>
                                                <p className="text-xs text-gray-500 dark:text-gray-400 max-w-xs mx-auto mb-4">
                                                    You will be redirected to PayPal to complete your purchase securely.
                                                </p>
                                            </div>
                                        )}

                                        {submitError && (
                                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3">
                                                <p className="text-sm text-red-600 dark:text-red-400">{submitError}</p>
                                            </div>
                                        )}

                                        <div className="pt-4">
                                            <button
                                                type="submit"
                                                disabled={processing}
                                                className="w-full py-4 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-sm shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 hover:scale-[1.01] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                                            >
                                                {processing ? (
                                                    <>
                                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ShieldCheckIcon className="h-5 w-5" />
                                                        {paymentMethod === 'bank_transfer' 
                                                            ? 'I have completed the transfer'
                                                            : paymentMethod === 'paypal'
                                                                ? 'Proceed to PayPal' 
                                                                : `Pay ${formattedAmount}`
                                                        }
                                                    </>
                                                )}
                                            </button>
                                            <p className="text-center text-[10px] text-gray-400 mt-3 flex items-center justify-center gap-1.5">
                                                <ShieldCheckIcon className="w-3 h-3 text-teal-500" />
                                                Payments are secure and encrypted
                                            </p>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Order Summary */}
                    <div className="lg:col-span-5">
                        <div className="sticky top-32">
                            <div className="relative overflow-hidden bg-gray-900 rounded-3xl border border-gray-800 shadow-2xl text-white p-8">
                                {/* Abstract background shapes */}
                                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                                <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>

                                <h3 className="relative text-lg font-bold mb-6 flex items-center gap-2">
                                    Order Summary
                                </h3>

                                <div className="relative space-y-4 mb-6 pb-6 border-b border-gray-800">
                                    {customerName && (
                                        <div className="mb-4 pb-4 border-b border-gray-800">
                                            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Customer</p>
                                            <p className="font-medium text-white">{customerName}</p>
                                            <p className="text-xs text-gray-400">{customerEmail}</p>
                                        </div>
                                    )}

                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-bold text-base capitalize">{plan} Plan</p>
                                            <p className="text-xs text-gray-400 capitalize">{formatBillingPeriod(billingPeriod)} billing</p>
                                        </div>
                                        <p className="font-bold text-base font-financial">{formattedAmount}</p>
                                    </div>
                                </div>

                                <div className="relative flex justify-between items-center text-xl font-bold mb-8">
                                    <span>Total</span>
                                    <span className="text-teal-400 font-financial">{formattedAmount}</span>
                                </div>

                                {/* Security & Trust Badges */}
                                <div className="relative bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 mb-6">
                                    <div className="flex items-center gap-3 mb-3">
                                        <ShieldCheckIcon className="w-5 h-5 text-teal-500" />
                                        <div>
                                            <p className="text-xs font-bold text-white">Secure Payment</p>
                                            <p className="text-[10px] text-gray-400">256-bit SSL Encrypted Connection</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-4 opacity-90 grayscale hover:grayscale-0 transition-all duration-300 items-center justify-start">
                                        {/* PayPal */}
                                        <img src={paypalLogo} alt="PayPal" className="h-5 w-auto object-contain" />
                                        {/* Mastercard */}
                                        <img src={mastercardLogo} alt="Mastercard" className="h-8 w-auto object-contain" />
                                        {/* Visa */}
                                        <img src={visaLogo} alt="Visa" className="h-4 w-auto object-contain" />
                                        {/* MTN */}
                                        <img src={mtnLogo} alt="MTN" className="h-8 w-auto object-contain" />
                                        {/* Airtel */}
                                        <img src={airtelLogo} alt="Airtel" className="h-6 w-auto object-contain" />
                                    </div>
                                </div>

                                <div className="relative space-y-3">
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <CheckCircleIcon className="w-3.5 h-3.5 text-teal-500/70" />
                                        <span>Instant activation</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-xs text-gray-400">
                                        <CheckCircleIcon className="w-3.5 h-3.5 text-teal-500/70" />
                                        <span>Secure data handling</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PaymentCheckout;
